/**
 * Lista newsletter ADMIN (Sprint 18, RF-04 / §8.6.4).
 * Paginazione SERVER-SIDE (10/pagina). I tab filtrano per bucket di stato via `?status=`;
 * i badge dei conteggi arrivano da GET /newsletters/status-counts (una sola chiamata, no N+1).
 */
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Card,
  Container,
  Table,
  TableBody,
  TableHead,
  TableContainer,
  TableFooter,
  TablePagination,
  Stack,
  TableCell,
  TableRow,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import SearchIcon from '@mui/icons-material/Search';
//--------------------------------------------------------
import { HttpErrorHandler, ErrorUI } from '../../../utils/ErrorHandler';
import { TablePaginationActions, theme } from '../../../utils/TableStyles';
import { CustomTextField } from '../../../utils/StyledInputs';
import { DefaultContainedButton, DefaultOutlinedButton } from '../../../utils/Buttons';
import { LoadingUI, EmptyPageUI } from '../../../utils/LayoutStyles';
import { newslettersApi } from '../../../services/newsletter/newslettersApi';
import { StatusBadge } from './components/StatusBadge';

/**
 * Tab di stato (default = Inviate). `key` = bucket lato BE (coincide con la chiave di status-counts
 * e col valore del parametro `?status=`); `all` non invia il filtro.
 */
const STATUS_TABS = [
  { key: 'all', label: 'Tutte', color: '#212B36', bg: '#E9ECEF' },
  { key: 'draft', label: 'Bozze', color: '#637381', bg: '#F4F6F8' },
  { key: 'scheduled', label: 'Programmate', color: '#329dd6', bg: '#d6f4f9' },
  { key: 'sending', label: 'In invio', color: '#ffa500', bg: '#FFF1D6' },
  { key: 'sent', label: 'Inviate', color: '#47ba25', bg: '#dbf6e5' },
  { key: 'terminated', label: 'Terminate', color: '#ff5630', bg: '#FFE7D9' },
];
const DEFAULT_TAB = STATUS_TABS.findIndex((t) => t.key === 'all');
const PER_PAGE = 10;
const POLL_MS = 8000;
/** Attesa prima di interrogare il BE mentre si digita nella ricerca per nome. */
const SEARCH_DEBOUNCE_MS = 500;

/**
 * Stati in cui l'invio è partito e quindi esistono statistiche da mostrare.
 * Allineato al dettaglio, FAILED compreso: è proprio lì che serve capire cosa è andato storto.
 */
const TRACKED_STATUSES = ['SENT', 'SENDING', 'STOPPED', 'FAILED'];
const hasTracking = (status) => TRACKED_STATUSES.includes(status);

/* Getter difensivi: nuovo modello con fallback al vecchio shape. */
const getStatus = (n) => n.status ?? n.status_name ?? null;
const getName = (n) => n.name ?? n.content?.[0]?.subject ?? '—';
const getSubject = (n) => n.subject ?? n.content?.[0]?.subject ?? '—';
const getScheduledAt = (n) => n.scheduled_at ?? null;

/** % di avanzamento invio dell'elemento di lista (null se non disponibile). */
const getProgressPercent = (n) => {
  const p = n.progress_percent ?? n.metrics?.progress_percent;
  if (p != null && !Number.isNaN(Number(p))) return Math.round(Number(p));
  const sent = n.sent ?? n.metrics?.sent;
  const included = n.included ?? n.metrics?.included;
  if (included) return Math.round((Number(sent || 0) / Number(included)) * 100);
  return null;
};

/** Indicatore circolare di avanzamento invio (determinato con %, altrimenti indeterminato). */
function SendingProgress({ percent }) {
  if (percent == null) return <CircularProgress size={28} thickness={5} color="primary" />;
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" value={percent} size={40} thickness={5} color="primary" />
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" sx={{ fontSize: '0.62rem', fontWeight: 700 }}>
          {`${percent}%`}
        </Typography>
      </Box>
    </Box>
  );
}
SendingProgress.propTypes = { percent: PropTypes.number };

export default function Newsletter() {
  const navigate = useNavigate();
  const is600Screen = useMediaQuery('(min-width: 600px)');

  const [newsletters, setNewsletters] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(PER_PAGE);
  const [ready, setReady] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentProfile, setCurrentProfile] = useState('');

  const [filterValue, setFilterValue] = useState(DEFAULT_TAB);
  const [page, setPage] = useState(0); // 0-based

  // Ricerca per nome: `searchQuery` è il valore digitato, `search` quello inviato al BE (debounced).
  const [searchQuery, setSearchQuery] = useState('');
  const [search, setSearch] = useState('');
  const searchTimerRef = useRef(null);

  // Riferimento sempre aggiornato alla lista corrente (per il polling senza ri-sottoscrizioni).
  const newslettersRef = useRef([]);
  newslettersRef.current = newsletters;

  // Carica la pagina corrente della lista (server-side).
  const loadList = useCallback((pageIndex, tabKey, searchTerm) => {
    const params = { page: pageIndex + 1 };
    if (tabKey && tabKey !== 'all') params.status = tabKey;
    if (searchTerm) params.search = searchTerm;

    setListLoading(true);
    newslettersApi
      .list(params)
      .then((res) => {
        const d = res.data ?? {};
        setNewsletters(Array.isArray(d) ? d : d.data ?? d.newsletters ?? []);
        setTotal(d.total ?? (Array.isArray(d) ? d.length : 0));
        if (d.per_page) setPerPage(d.per_page);
        setErrorMessage('');
      })
      .catch((error) => HttpErrorHandler(error, setErrorMessage))
      .finally(() => {
        setListLoading(false);
        setReady(true);
      });
  }, []);

  // Carica i conteggi per i badge dei tab (una sola chiamata).
  const loadCounts = useCallback((searchTerm) => {
    newslettersApi
      .statusCounts(searchTerm ? { search: searchTerm } : undefined)
      .then((res) => setCounts(res.data ?? {}))
      .catch(() => {
        /* badge non critici: in caso di errore restano a 0 */
      });
  }, []);

  // Profilo corrente (guard ADMIN)
  useEffect(() => {
    let data;
    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));
    if (data) setCurrentProfile(data.roles.filter((role) => role.last_used));
  }, []);

  // Conteggi all'ingresso e ad ogni cambio del termine di ricerca (i badge seguono la lista filtrata)
  useEffect(() => {
    loadCounts(search);
  }, [search, loadCounts]);

  // Lista al cambio pagina/tab/ricerca
  useEffect(() => {
    loadList(page, STATUS_TABS[filterValue].key, search);
  }, [page, filterValue, search, loadList]);

  // Cleanup del timer di debounce allo smontaggio
  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  // Polling mentre ci sono invii in corso: aggiorna badge (e la lista se sono sul tab "In invio").
  const hasSending = (counts.sending ?? 0) > 0;
  useEffect(() => {
    if (!hasSending) return undefined;
    const timer = setInterval(() => {
      loadCounts(search);
      const tabKey = STATUS_TABS[filterValue].key;
      // Ricarica la lista se sei sul tab "In invio" o se una riga visibile è in SENDING
      // (così la % inline si aggiorna in qualsiasi tab).
      const listHasSending = newslettersRef.current.some((n) => getStatus(n) === 'SENDING');
      if (tabKey === 'sending' || listHasSending) loadList(page, tabKey, search);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [hasSending, filterValue, page, search, loadCounts, loadList]);

  const handleChangeFilterTab = (event, newValue) => {
    setFilterValue(newValue);
    setPage(0);
  };
  const handleChangePage = (event, newPage) => setPage(newPage);

  // Destinazioni raggiungibili sia dalla riga sia dalle icone di azione.
  const goToDetail = (id) => navigate('/admin/newsletter/newsletter-details', { state: { newsID: id } });
  const goToTracking = (id) => navigate('/admin/newsletter/tracking', { state: { newsID: id } });

  // Digitazione nella ricerca: aggiorna subito l'input, interroga il BE dopo il debounce.
  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchQuery(value);

    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      setSearch(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  return (
    <>
      <Helmet>
        <title>Newsletter</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} sx={{ paddingBottom: '24px' }}>
          <DefaultOutlinedButton
            startIcon={<DescriptionOutlinedIcon />}
            onClick={() => navigate('/admin/newsletter/templates')}
          >
            Template
          </DefaultOutlinedButton>
          <DefaultContainedButton startIcon={<AddIcon />} onClick={() => navigate('/admin/newsletter/new')}>
            Crea Newsletter
          </DefaultContainedButton>
        </Stack>

        {ready ? (
          errorMessage.length === 0 ? (
            <Card>
              <Tabs
                value={filterValue}
                onChange={handleChangeFilterTab}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                textColor="secondary"
                indicatorColor="secondary"
                sx={{ boxShadow: 'rgba(145, 158, 171, 0.08) 0px -2px 0px 0px inset', padding: '0px 24px 0' }}
              >
                {STATUS_TABS.map((tab, index) => {
                  const active = filterValue === index;
                  return (
                    <Tab
                      key={tab.key}
                      disableRipple
                      iconPosition="end"
                      label={tab.label}
                      icon={
                        <Typography
                          sx={{
                            backgroundColor: active ? tab.color : tab.bg,
                            color: active ? '#ffffff' : tab.color,
                            borderRadius: '6px',
                            padding: '2px 8px',
                            fontSize: '1em',
                            fontWeight: 'bold',
                          }}
                        >
                          {counts[tab.key] ?? 0}
                        </Typography>
                      }
                    />
                  );
                })}
              </Tabs>

              <Stack direction="row" alignItems="center" sx={{ padding: '20px 20px 0' }}>
                <CustomTextField
                  size="small"
                  placeholder="Cerca newsletter per nome"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#637381' }} />
                      </InputAdornment>
                    ),
                  }}
                  customStyles={{ maxWidth: 400 }}
                />
              </Stack>

              <TableContainer sx={{ padding: '20px' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      {is600Screen && <TableCell>Oggetto</TableCell>}
                      <TableCell align="center">Stato</TableCell>
                      {is600Screen && <TableCell>Programmata per</TableCell>}
                      <TableCell align="right">Azioni</TableCell>
                    </TableRow>
                  </TableHead>

                  {/* eslint-disable-next-line no-nested-ternary */}
                  {listLoading ? (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : newsletters.length > 0 ? (
                    <TableBody>
                      {newsletters.map((newsletter) => (
                        <TableRow
                          hover
                          key={newsletter.id}
                          onClick={() => goToDetail(newsletter.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{getName(newsletter)}</TableCell>
                          {is600Screen && <TableCell>{getSubject(newsletter)}</TableCell>}
                          <TableCell align="center">
                            {getStatus(newsletter) === 'SENDING' ? (
                              <SendingProgress percent={getProgressPercent(newsletter)} />
                            ) : (
                              <StatusBadge status={getStatus(newsletter)} />
                            )}
                          </TableCell>
                          {is600Screen && (
                            <TableCell>
                              {getScheduledAt(newsletter)
                                ? dayjs(getScheduledAt(newsletter)).format('DD/MM/YYYY HH:mm')
                                : '—'}
                            </TableCell>
                          )}
                          <TableCell align="right">
                            {/* stopPropagation: la riga è già cliccabile, senza di esso
                                ogni icona scatenerebbe anche la navigazione della riga. */}
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Apri la newsletter" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    goToDetail(newsletter.id);
                                  }}
                                >
                                  <VisibilityOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {/* Le statistiche esistono solo da quando l'invio è partito. */}
                              <Tooltip
                                title={
                                  hasTracking(getStatus(newsletter))
                                    ? 'Statistiche di invio'
                                    : 'Statistiche disponibili dopo l’invio'
                                }
                                arrow
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!hasTracking(getStatus(newsletter))}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      goToTracking(newsletter.id);
                                    }}
                                  >
                                    <InsightsIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={5}>
                          {EmptyPageUI(
                            search ? `Nessuna newsletter trovata per "${search}"` : 'Nessuna newsletter disponibile'
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}

                  {total > perPage && (
                    <TableFooter>
                      <TableRow>
                        <ThemeProvider theme={theme}>
                          <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[]}
                            colSpan={5}
                            count={total}
                            rowsPerPage={perPage}
                            page={page}
                            onPageChange={handleChangePage}
                            labelRowsPerPage="Righe per Pagina"
                            ActionsComponent={TablePaginationActions}
                          />
                        </ThemeProvider>
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </TableContainer>
            </Card>
          ) : (
            ErrorUI(errorMessage)
          )
        ) : (
          LoadingUI()
        )}
      </Container>
    </>
  );
}
